import type { ToolCallLabels } from './toolcalls.types';
import {
  TERMINAL_TOOL_NAMES,
  TERMINAL_TOOL_PRESENTATION,
} from './external-tool.types';

type ToolCallLabelInput = {
  toolName?: string;
  message?: string;
  labelPresent?: string;
  labelPast?: string;
};

type StoredToolCallLabels = Pick<ToolCallLabels, 'labelPresent' | 'labelPast'>;

const TOOL_CALL_LABELS: Record<string, StoredToolCallLabels> = {
  'http-tool': {
    labelPresent: 'Executing HTTP request...',
    labelPast: 'Sent HTTP request',
  },
  'dynamic-http-tool': {
    labelPresent: 'Executing HTTP request...',
    labelPast: 'Sent HTTP request',
  },
  'validated-http-tool': {
    labelPresent: 'Executing HTTP request...',
    labelPast: 'Sent HTTP request',
  },
  'http-executor': {
    labelPresent: 'Sending HTTP request...',
    labelPast: 'Sent HTTP request',
  },
  ...Object.fromEntries(
    TERMINAL_TOOL_NAMES.map((toolName) => [
      toolName,
      {
        labelPresent: TERMINAL_TOOL_PRESENTATION[toolName].runningText,
        labelPast: TERMINAL_TOOL_PRESENTATION[toolName].labelPast,
      },
    ]),
  ),
  'python-tool': {
    labelPresent: 'Running Python...',
    labelPast: 'Ran Python',
  },
  'coder-tool': {
    labelPresent: 'Running Python...',
    labelPast: 'Ran Python',
  },
  authenticate: {
    labelPresent: 'Authenticating credential...',
    labelPast: 'Authenticated credential',
  },
  'file-upload-client': {
    labelPresent: 'Uploading file...',
    labelPast: 'File Upload Tool',
  },
  'prepare-file': {
    labelPresent: 'Preparing file...',
    labelPast: 'Prepare File',
  },
  'code-analyzer-tool': {
    labelPresent: 'Analyzing code...',
    labelPast: 'Analyzed code',
  },
  grep: {
    labelPresent: 'Searching code...',
    labelPast: 'Searched code',
  },
  read: {
    labelPresent: 'Reading file...',
    labelPast: 'Read file',
  },
  glob: {
    labelPresent: 'Listing files...',
    labelPast: 'Listed files',
  },
  bash: {
    labelPresent: 'Running command...',
    labelPast: 'Ran command',
  },
  'trace-investigation-tool': {
    labelPresent: 'Investigating trace...',
    labelPast: 'Investigated trace',
  },
  trace_investigation: {
    labelPresent: 'Investigating trace...',
    labelPast: 'Investigated trace',
  },
  'browser-tool': {
    labelPresent: 'Using browser...',
    labelPast: 'Used browser',
  },
  'navigate-web': {
    labelPresent: 'Navigating browser...',
    labelPast: 'Navigated browser',
  },
  'login-tool': {
    labelPresent: 'Logging in...',
    labelPast: 'Logged in',
  },
  'get-authentication-details': {
    labelPresent: 'Authenticating...',
    labelPast: 'Authenticated',
  },
  'web-search-tool': {
    labelPresent: 'Searching the web...',
    labelPast: 'Searched the web',
  },
  'websearch-tool': {
    labelPresent: 'Searching the web...',
    labelPast: 'Searched the web',
  },
  'load-tools': {
    labelPresent: 'Loading tools...',
    labelPast: 'Loaded tools',
  },
  'signals-semantic-search-tool': {
    labelPresent: 'Searching signals...',
    labelPast: 'Searched signals',
  },
  'finding-semantic-search-tool': {
    labelPresent: 'Searching findings...',
    labelPast: 'Searched findings',
  },
  'findings-semantic-search-tool': {
    labelPresent: 'Searching findings...',
    labelPast: 'Searched findings',
  },
  'signals-explorer-tool': {
    labelPresent: 'Searching signals...',
    labelPast: 'Searched signals',
  },
  'findings-explorer-tool': {
    labelPresent: 'Searching findings...',
    labelPast: 'Searched findings',
  },
  'endpoints-explorer-tool': {
    labelPresent: 'Searching endpoints...',
    labelPast: 'Searched endpoints',
  },
  'search-tracked-endpoints': {
    labelPresent: 'Searching tracked endpoints...',
    labelPast: 'Searched tracked endpoints',
  },
  'get-tracked-endpoint-schema': {
    labelPresent: 'Reading endpoint schema...',
    labelPast: 'Read endpoint schema',
  },
  'search-tracked-web-pages': {
    labelPresent: 'Searching web pages...',
    labelPast: 'Searched web pages',
  },
  'list-assets-tool': {
    labelPresent: 'Listing assets...',
    labelPast: 'Listed assets',
  },
  'get-tracked-web-page': {
    labelPresent: 'Reading web page...',
    labelPast: 'Read web page',
  },
  'list-endpoints-on-web-page': {
    labelPresent: 'Finding related endpoints...',
    labelPast: 'Found related endpoints',
  },
  'list-web-pages-for-endpoint': {
    labelPresent: 'Finding related webpages...',
    labelPast: 'Found related webpages',
  },
  'feature-analysis-tool': {
    labelPresent: 'Looking up product features...',
    labelPast: 'Looked up product features',
  },
  'executions-explorer-tool': {
    labelPresent: 'Searching executions...',
    labelPast: 'Searched executions',
  },
  'har-explorer-tool': {
    labelPresent: 'Searching network log...',
    labelPast: 'Searched network log',
  },
  'long-term-memory-tool': {
    labelPresent: 'Recalling memory...',
    labelPast: 'Recalled memory',
  },
  'get-memory-tool': {
    labelPresent: 'Recalling memory...',
    labelPast: 'Recalled memory',
  },
  get_neo_memories: {
    labelPresent: 'Recalling memory...',
    labelPast: 'Recalled memory',
  },
  'create-memory-tool': {
    labelPresent: 'Saving memory...',
    labelPast: 'Saved memory',
  },
  'proxy-control-tool': {
    labelPresent: 'Running proxy control...',
    labelPast: 'Updated proxy',
  },
  'intercept-toggle-tool': {
    labelPresent: 'Updating intercept...',
    labelPast: 'Updated intercept',
  },
  'intercept-rule-tool': {
    labelPresent: 'Updating intercept rules...',
    labelPast: 'Updated intercept rules',
  },
  'match-replace-rule-tool': {
    labelPresent: 'Updating match/replace rules...',
    labelPast: 'Updated match/replace rules',
  },
  'replay-request-tool': {
    labelPresent: 'Sending replay request...',
    labelPast: 'Sent replay request',
  },
  'launch-browser-tool': {
    labelPresent: 'Launching browser...',
    labelPast: 'Launched browser',
  },
  'browser-automation-tool': {
    labelPresent: 'Running browser step...',
    labelPast: 'Ran browser step',
  },
  'read-captured-proxy-requests': {
    labelPresent: 'Reading captured proxy requests...',
    labelPast: 'Read captured proxy requests',
  },
  'read-file': {
    labelPresent: 'Reading file...',
    labelPast: 'Read file',
  },
  read_file: {
    labelPresent: 'Reading file...',
    labelPast: 'Read file',
  },
  'write-file': {
    labelPresent: 'Writing file...',
    labelPast: 'Wrote file',
  },
  write_file: {
    labelPresent: 'Writing file...',
    labelPast: 'Wrote file',
  },
  ls: {
    labelPresent: 'Listing files...',
    labelPast: 'Listed files',
  },
  validator: {
    labelPresent: 'Validating findings...',
    labelPast: 'Validated findings',
  },
  operator: {
    labelPresent: 'Executing operations...',
    labelPast: 'Executed operations',
  },
  intelligence: {
    labelPresent: 'Researching...',
    labelPast: 'Researched',
  },
  recon: {
    labelPresent: 'Reconning the target...',
    labelPast: 'Recon complete',
  },
  'application-data-analyst': {
    labelPresent: 'Exploring environment...',
    labelPast: 'Explored environment',
  },
  'object-creator': {
    labelPresent: 'Creating entities...',
    labelPast: 'Created entities',
  },
  'code-analyst': {
    labelPresent: 'Analyzing source code...',
    labelPast: 'Analyzed source code',
  },
  general: {
    labelPresent: 'Working...',
    labelPast: 'Finished the task',
  },
  'background-attack': {
    labelPresent: 'Launching background attack...',
    labelPast: 'Launched background attack',
  },
  'skill-builder': {
    labelPresent: 'Building skill...',
    labelPast: 'Built skill',
  },
  'attacker-tool': {
    labelPresent: 'Running attack simulation...',
    labelPast: 'Ran attack simulation',
  },
  'propose-skill-draft-update': {
    labelPresent: 'Updating skill draft...',
    labelPast: 'Updated skill draft',
  },
  propose_skill_draft_update: {
    labelPresent: 'Updating skill draft...',
    labelPast: 'Updated skill draft',
  },
  mcp__terra__terra_query: {
    labelPresent: 'Scanning data...',
    labelPast: 'Scanned data',
  },
  mcp__terra__terra_query_validate: {
    labelPresent: 'Checking query...',
    labelPast: 'Checked query',
  },
  mcp__terra__terra_query_schema: {
    labelPresent: 'Reading data catalog...',
    labelPast: 'Read data catalog',
  },
};

const PAST_TENSE_REPLACEMENTS: Array<[RegExp, string]> = [
  [/^Searching\b/i, 'Searched'],
  [/^Reading\b/i, 'Read'],
  [/^Running\b/i, 'Ran'],
  [/^Fetching\b/i, 'Fetched'],
  [/^Writing\b/i, 'Wrote'],
  [/^Executing\b/i, 'Executed'],
  [/^Creating\b/i, 'Created'],
  [/^Updating\b/i, 'Updated'],
  [/^Generating\b/i, 'Generated'],
  [/^Analyzing\b/i, 'Analyzed'],
  [/^Investigating\b/i, 'Investigated'],
  [/^Listing\b/i, 'Listed'],
  [/^Saving\b/i, 'Saved'],
  [/^Recording\b/i, 'Recorded'],
  [/^Recalling\b/i, 'Recalled'],
  [/^Navigating\b/i, 'Navigated'],
  [/^Logging in\b/i, 'Logged in'],
  [/^Building\b/i, 'Built'],
  [/^Validating\b/i, 'Validated'],
  [/^Gathering\b/i, 'Gathered'],
  [/^Reconning\b/i, 'Reconned'],
];

export function normalizeToolCallLabelName(toolName?: string): string {
  return (toolName ?? '').replace(/_/g, '-').toLowerCase();
}

export const MCP_TOOL_PREFIX = 'mcp__';

export function isMcpToolName(toolName?: string): boolean {
  return (toolName ?? '').startsWith(MCP_TOOL_PREFIX);
}

export function humanizeMcpToolName(toolName: string): string {
  const withoutPrefix = toolName.replace(/^mcp__/, '');
  const parts = withoutPrefix.split('__');

  if (parts.length < 2) {
    const serverName = parts[0] ?? withoutPrefix;

    return serverName.charAt(0).toUpperCase() + serverName.slice(1);
  }

  const serverName = parts[0];
  const toolPart = parts.slice(1).join('_').replace(/_/g, ' ');
  const humanTool = toolPart
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
  const humanServer = serverName.charAt(0).toUpperCase() + serverName.slice(1);

  return `${humanServer}: ${humanTool}`;
}

export function inferPastToolCallLabel(labelPresent: string): string {
  const label = labelPresent.trim().replace(/\.\.\.$/, '');

  for (const [pattern, replacement] of PAST_TENSE_REPLACEMENTS) {
    if (pattern.test(label)) {
      return label.replace(pattern, replacement);
    }
  }

  return label;
}

const OBJECT_ID_IN_LABEL = /[a-f0-9]{24}/i;

function hasObjectIdInLabel(value?: string): boolean {
  return Boolean(value && OBJECT_ID_IN_LABEL.test(value));
}

function withoutObjectIds(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }

  const stripped = value
    .replace(/[a-f0-9]{24}/gi, '')
    .replace(/\s*[:|-]\s*(?:\.\.\.)?$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return stripped.length > 0 ? stripped : undefined;
}

const INTERNAL_STATUS_LABELS = new Set([
  'starting',
  'started',
  'running',
  'in-progress',
  'in_progress',
  'waiting_input',
  'waiting-input',
  'completed',
  'error',
  'aborted',
]);

function withoutInternalStatus(value?: string): string | undefined {
  return value &&
    INTERNAL_STATUS_LABELS.has(value.trim().replace(/\.+$/, '').toLowerCase())
    ? undefined
    : value;
}

const FAILED_VERB_INFINITIVES: ReadonlyArray<readonly [string, string]> = [
  ['Looking up', 'look up'],
  ['Logging in', 'log in'],
  ['Searching', 'search'],
  ['Reading', 'read'],
  ['Listing', 'list'],
  ['Running', 'run'],
  ['Executing', 'execute'],
  ['Sending', 'send'],
  ['Authenticating', 'authenticate'],
  ['Uploading', 'upload'],
  ['Preparing', 'prepare'],
  ['Analyzing', 'analyze'],
  ['Investigating', 'investigate'],
  ['Using', 'use'],
  ['Navigating', 'navigate'],
  ['Finding', 'find'],
  ['Recalling', 'recall'],
  ['Saving', 'save'],
  ['Updating', 'update'],
  ['Launching', 'launch'],
  ['Writing', 'write'],
  ['Validating', 'validate'],
  ['Researching', 'research'],
  ['Reconning', 'recon'],
  ['Exploring', 'explore'],
  ['Creating', 'create'],
  ['Building', 'build'],
  ['Scanning', 'scan'],
  ['Checking', 'check'],
  ['Starting', 'start'],
  ['Stopping', 'stop'],
  ['Driving', 'drive'],
  ['Fetching', 'fetch'],
  ['Generating', 'generate'],
  ['Gathering', 'gather'],
  ['Recording', 'record'],
];

export function inferFailedToolCallLabel(labelPresent: string): string {
  const label = labelPresent
    .trim()
    .replace(/\.\.\.$/, '')
    .replace(/\.$/, '');

  if (label.length === 0 || label.toLowerCase() === 'working') {
    return 'Tried this step';
  }

  const match = FAILED_VERB_INFINITIVES.find(([gerund]) =>
    label.toLowerCase().startsWith(gerund.toLowerCase()),
  );

  if (!match) {
    return `Tried to ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
  }

  const rest = label.slice(match[0].length).trim();

  return rest.length > 0
    ? `Tried to ${match[1]} ${rest}`
    : `Tried to ${match[1]}`;
}

function withFailedLabel(labels: StoredToolCallLabels): ToolCallLabels {
  return {
    ...labels,
    labelFailed: inferFailedToolCallLabel(labels.labelPresent),
  };
}

export function resolveToolCallLabels({
  toolName,
  message: rawMessage,
  labelPresent: rawLabelPresent,
  labelPast: rawLabelPast,
}: ToolCallLabelInput): ToolCallLabels {
  const message = withoutInternalStatus(rawMessage);
  const labelPresent = withoutInternalStatus(rawLabelPresent);
  const labelPast = withoutInternalStatus(rawLabelPast);
  const normalizedToolName = normalizeToolCallLabelName(toolName);

  const configuredLabels =
    TOOL_CALL_LABELS[normalizedToolName] ?? TOOL_CALL_LABELS[toolName ?? ''];

  const usablePresent = hasObjectIdInLabel(labelPresent)
    ? undefined
    : labelPresent;
  const usablePast = hasObjectIdInLabel(labelPast) ? undefined : labelPast;
  const usableMessage = hasObjectIdInLabel(message)
    ? withoutObjectIds(message)
    : message;

  if (isMcpToolName(toolName) && !configuredLabels) {
    const humanName = humanizeMcpToolName(toolName ?? '');
    const mcpPresent = `Using ${humanName}...`;

    return withFailedLabel({
      labelPresent: usablePresent ?? mcpPresent,
      labelPast: usablePast ?? `Used ${humanName}`,
    });
  }

  const resolvedLabelPresent =
    usablePresent ??
    configuredLabels?.labelPresent ??
    (usableMessage
      ? `${usableMessage.replace(/\.\.\.$/, '')}...`
      : 'Working...');

  return withFailedLabel({
    labelPresent: resolvedLabelPresent,
    labelPast:
      usablePast ??
      configuredLabels?.labelPast ??
      inferPastToolCallLabel(resolvedLabelPresent),
  });
}
